import React, { useState, useRef } from 'react';
import styles from '../../../styles/ListingManagement.module.css';
import type { ListingFilters, ListingSummary } from '../../../types/ListingTypes';
import { useNavigate } from 'react-router-dom';

interface ListingProps {
    listings: ListingSummary[];
    loading: any;
    error: any;
}

const ListingTable: React.FC<ListingProps> = ({ listings, loading, error }) => {
    const navigate = useNavigate();    
    const [colWidths, setColWidths] = useState<number[]>([280, 140, 110, 100, 120, 120, 160]);
    const resizingRef = useRef<{ colIndex: number; startX: number; startWidth: number } | null>(null);

    const handleResizeMouseDown = (e: React.MouseEvent, colIndex: number) => {
        e.preventDefault();
        resizingRef.current = { colIndex, startX: e.clientX, startWidth: colWidths[colIndex] };
        const onMouseMove = (moveEvent: MouseEvent) => {
            if (!resizingRef.current) return;
            const { colIndex, startX, startWidth } = resizingRef.current;
            const delta = moveEvent.clientX - startX;
            const newWidth = Math.max(60, startWidth + delta);
            setColWidths((prev) => {
                const next = [...prev];
                next[colIndex] = newWidth;
                return next;
            });
        };
        const onMouseUp = () => {
            resizingRef.current = null;
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    return (
        <div>
            <section className={styles.transactionSection}>
                {loading && <p>Loading listings...</p>}
                {error && <p className={styles.transactionEmpty}>{error}</p>}
                {!loading && !error && listings.length === 0 && (
                    <div className={styles.transactionEmpty}>No listings found.</div>
                )}
                {!loading && listings.length > 0 && (
                    <table className={styles.listingTable}>
                        <colgroup>
                            {colWidths.map((w, i) => (
                                <col key={i} style={{ width: w }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {(['Address', 'City', 'Province', 'Type', 'Status', 'Listed Clients', 'Agent'] as const).map((label, i) => (
                                    <th key={label}>
                                        {label}
                                        <div
                                            className={styles.resizeHandle}
                                            onMouseDown={(e) => handleResizeMouseDown(e, i)}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {listings.map((listing) => {
                                const address = [
                                    listing.complexName,
                                    listing.unitNumber ? `Unit ${listing.unitNumber}` : undefined,
                                    listing.streetNumber,
                                    listing.streetName,
                                ]
                                    .filter(Boolean)
                                    .join(' ');
                                const statusClass = listing.status?.toLowerCase() as 'open' | 'pending' | 'closed' | 'incomplete';
                                const agentName = listing.agent
                                    ? `${listing.agent.fullNames} ${listing.agent.lastName}`.trim()
                                    : '—';
                                return (
                                    <tr key={listing.id} onClick={() => navigate(`/ListingViewer/${listing.id}`)}>
                                        <td>{address || '—'}</td>
                                        <td>{listing.city || '—'}</td>
                                        <td>{listing.province || '—'}</td>
                                        <td>{listing.type}</td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${styles[statusClass] ?? ''}`}>
                                                {listing.status}
                                            </span>
                                        </td>
                                        <td>{listing.clientIds?.length ?? 0}</td>
                                        <td>{agentName}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

export default ListingTable;